import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, IconButton, Avatar, Chip, Divider, CircularProgress, Badge,
  InputAdornment, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText,
  Snackbar, Alert, Tabs, Tab, ListItemButton, Paper, useMediaQuery, useTheme,
  Drawer, SwipeableDrawer,
} from '@mui/material';
import {
  Delete, Send, AttachFile, Close, Group, People, Chat as ChatIcon,
  PersonAdd, Search, Share, Download, ContentCopy, OpenInNew,
  CheckCircle, InsertDriveFile, PictureAsPdf, Description, TableChart,
  Code, Movie, MusicNote, ArrowBack, Refresh, ExpandMore,
} from '@mui/icons-material';
import api from '../../api/axiosInterceptor';
import store from '../../store';
// import { refreshAccessToken } from '../../api/api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getWsBase() {
  const httpBase = api.defaults.baseURL || '';
  return httpBase
    .replace(/^http/, 'ws')
    .replace(/\/v\d+\/api\/?$/, '')
    .replace(/\/api\/?$/, '');
}

// async function getToken() {
//   let token = store.getState().auth.token;
//   if (!token) token = await refreshAccessToken();
//   return token;
// }

function getToken() {
  return store.getState().auth.token;
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function groupMessagesByDate(messages) {
  const groups = {};
  messages.forEach(msg => {
    const key = formatDate(msg.created);
    if (!groups[key]) groups[key] = [];
    groups[key].push(msg);
  });
  return groups;
}

function getLastMessageTime(item) {
  const msgTime = item?.last_message?.created;
  return msgTime ? new Date(msgTime).getTime() : new Date(item?.created || 0).getTime();
}

function getFileCategory(name = '') {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
  if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return 'video';
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'word';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
  if (['ppt', 'pptx'].includes(ext)) return 'ppt';
  if (['zip', 'rar', '7z'].includes(ext)) return 'archive';
  if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return 'audio';
  if (['js', 'ts', 'py', 'json', 'html', 'css', 'java', 'cpp', 'c'].includes(ext)) return 'code';
  return 'file';
}

function getCategoryFromUrl(url = '') {
  const clean = url.split('?')[0];
  const ext = (clean.split('.').pop() || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
  if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return 'video';
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx'].includes(ext)) return 'word';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'excel';
  if (['ppt', 'pptx'].includes(ext)) return 'ppt';
  if (['zip', 'rar', '7z'].includes(ext)) return 'archive';
  if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return 'audio';
  if (['js', 'ts', 'py', 'json', 'html', 'css'].includes(ext)) return 'code';
  return null;
}

function getFilenameFromUrl(url = '') {
  try {
    const clean = url.split('?')[0];
    const parts = clean.split('/');
    return decodeURIComponent(parts[parts.length - 1] || url);
  } catch {
    return url;
  }
}

function parseTextForLinks(text = '') {
  const urlRegex = /(https?:\/\/[^\s]+|(?:[\w-]+\.)+[a-z]{2,6}(?:\/[^\s]*)?)/gi;
  const matches = [];
  let match;
  while ((match = urlRegex.exec(text)) !== null) {
    let url = match[0];
    if (!url.startsWith('http')) url = 'https://' + url;
    matches.push({ url, index: match.index, length: match[0].length });
  }
  return matches;
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const FILE_CONFIG = {
  pdf:     { color: '#dc2626', bg: '#fef2f2', icon: PictureAsPdf,   label: 'PDF' },
  word:    { color: '#2563eb', bg: '#eff6ff', icon: Description,     label: 'Word' },
  excel:   { color: '#059669', bg: '#f0fdf4', icon: TableChart,      label: 'Excel' },
  ppt:     { color: '#d97706', bg: '#fffbeb', icon: InsertDriveFile, label: 'PowerPoint' },
  archive: { color: '#78716c', bg: '#faf5f0', icon: InsertDriveFile, label: 'Archive' },
  audio:   { color: '#7c3aed', bg: '#f5f3ff', icon: MusicNote,       label: 'Audio' },
  video:   { color: '#0891b2', bg: '#ecfeff', icon: Movie,           label: 'Video' },
  code:    { color: '#374151', bg: '#f9fafb', icon: Code,            label: 'Code' },
  image:   { color: '#ec4899', bg: '#fdf2f8', icon: InsertDriveFile, label: 'Image' },
  file:    { color: '#6b7280', bg: '#f9fafb', icon: InsertDriveFile, label: 'File' },
};

const AVATAR_COLORS = ['#7c3aed', '#2563eb', '#059669', '#db2777', '#d97706', '#dc2626'];
function getAvatarColor(str = '') {
  if (!str) return AVATAR_COLORS[0];
  return AVATAR_COLORS[str.charCodeAt(0) % AVATAR_COLORS.length];
}
function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase() || '?';
}

// ─── Message Limit Options ─────────────────────────────────────────────────────

const MSG_LIMIT_OPTIONS = [
  { value: 20,       label: '20 messages' },
  { value: 50,       label: '50 messages' },
  { value: 100,      label: '100 messages' },
  { value: 'all',    label: 'All messages' },
  { value: 'custom', label: 'Custom…' },
];

// ─── MessageLimitSelector ─────────────────────────────────────────────────────

function MessageLimitSelector({ value, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const [customInput, setCustomInput] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const customRef = useRef(null);

  const currentLabel = value === 'all'
    ? 'All'
    : typeof value === 'number'
      ? `${value} msgs`
      : String(value);

  function handleSelect(opt) {
    if (opt.value === 'custom') {
      setShowCustom(true);
      setAnchor(null);
      setCustomInput('');
      setTimeout(() => customRef.current?.focus(), 80);
    } else {
      onChange(opt.value);
      setAnchor(null);
      setShowCustom(false);
    }
  }

  function applyCustom() {
    const n = parseInt(customInput, 10);
    if (n > 0) {
      onChange(n);
      setShowCustom(false);
      setCustomInput('');
    }
  }

  if (showCustom) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <TextField
          inputRef={customRef}
          size="small"
          type="number"
          placeholder="e.g. 75"
          value={customInput}
          onChange={e => setCustomInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') applyCustom();
            if (e.key === 'Escape') setShowCustom(false);
          }}
          inputProps={{ min: 1, max: 9999 }}
          sx={{
            width: 88,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: 12,
              height: 30,
              bgcolor: '#f5f5f7',
              '&.Mui-focused fieldset': { borderColor: '#c4b5fd' },
            },
          }}
        />
        <Button
          size="small"
          variant="contained"
          onClick={applyCustom}
          disabled={!customInput || parseInt(customInput, 10) < 1}
          sx={{
            minWidth: 'unset', px: 1.2, fontSize: 11, borderRadius: 1.5,
            bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' }, height: 30,
          }}>
          OK
        </Button>
        <IconButton size="small" onClick={() => setShowCustom(false)} sx={{ color: '#bbb', width: 26, height: 26 }}>
          <Close sx={{ fontSize: 14 }} />
        </IconButton>
      </Box>
    );
  }

  return (
    <>
      <Tooltip title="Message load limit">
        <Button
          size="small"
          onClick={e => setAnchor(e.currentTarget)}
          endIcon={<ExpandMore sx={{ fontSize: 13 }} />}
          sx={{
            fontSize: 11,
            fontWeight: 600,
            color: '#7c3aed',
            bgcolor: '#f0eeff',
            border: '1px solid #c4b5fd',
            borderRadius: 2,
            px: 1.2,
            height: 30,
            textTransform: 'none',
            flexShrink: 0,
            '&:hover': { bgcolor: '#ede9fe', borderColor: '#a78bfa' },
          }}>
          {currentLabel}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', minWidth: 155 } }}>
        {MSG_LIMIT_OPTIONS.map(opt => (
          <MenuItem
            key={opt.value}
            onClick={() => handleSelect(opt)}
            selected={opt.value === value}
            sx={{
              fontSize: 13,
              py: 0.9,
              gap: 1,
              '&.Mui-selected': { bgcolor: '#f0eeff', color: '#7c3aed', fontWeight: 700 },
              '&.Mui-selected:hover': { bgcolor: '#ede9fe' },
            }}>
            {opt.label}
            {opt.value === value && <CheckCircle sx={{ fontSize: 14, ml: 'auto', color: '#7c3aed' }} />}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// ─── UserAvatar ───────────────────────────────────────────────────────────────

function UserAvatar({ name = '', size = 40 }) {
  const color = getAvatarColor(name);
  return (
    <Avatar sx={{
      width: size, height: size,
      bgcolor: color + '22', color,
      border: `1.5px solid ${color}44`,
      fontSize: size * 0.35, fontWeight: 700,
    }}>
      {getInitials(name)}
    </Avatar>
  );
}

// ─── ShareMenu ────────────────────────────────────────────────────────────────

function ShareMenu({ url, name, onSnack }) {
  const [anchor, setAnchor] = useState(null);

  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(url); onSnack('Link copied to clipboard!', 'success'); }
    catch { onSnack('Failed to copy link', 'error'); }
    setAnchor(null);
  };
  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = url; a.download = name || 'file'; a.target = '_blank'; a.click();
    setAnchor(null);
  };
  const handleOpen = () => { window.open(url, '_blank'); setAnchor(null); };

  return (
    <>
      <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
        <Share fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={handleCopy}><ListItemIcon><ContentCopy fontSize="small" /></ListItemIcon><ListItemText>Copy link</ListItemText></MenuItem>
        <MenuItem onClick={handleDownload}><ListItemIcon><Download fontSize="small" /></ListItemIcon><ListItemText>Download</ListItemText></MenuItem>
        <MenuItem onClick={handleOpen}><ListItemIcon><OpenInNew fontSize="small" /></ListItemIcon><ListItemText>Open in new tab</ListItemText></MenuItem>
      </Menu>
    </>
  );
}

// ─── Link File Card ───────────────────────────────────────────────────────────

function LinkFileCard({ url, isMe, onSnack }) {
  const category = getCategoryFromUrl(url);
  if (!category) return null;
  const cfg = FILE_CONFIG[category] || FILE_CONFIG.file;
  const IconComp = cfg.icon;
  const name = getFilenameFromUrl(url);

  if (category === 'image') {
    return (
      <Box sx={{ mt: 0.5 }}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          <Box component="img" src={url} alt={name}
            sx={{ maxWidth: 220, maxHeight: 170, borderRadius: 2, display: 'block', objectFit: 'cover', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.08)' }}
            onError={e => { e.target.style.display = 'none'; }} />
        </a>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.3 }}><ShareMenu url={url} name={name} onSnack={onSnack} /></Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '8px 12px', mt: 0.5, borderRadius: 2, minWidth: 180, maxWidth: 260, bgcolor: isMe ? 'rgba(255,255,255,0.12)' : cfg.bg, border: isMe ? '1px solid rgba(255,255,255,0.18)' : `1px solid ${cfg.color}22` }}>
      <Box sx={{ width: 36, height: 36, borderRadius: 1.5, flexShrink: 0, bgcolor: isMe ? 'rgba(255,255,255,0.18)' : cfg.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${isMe ? 'rgba(255,255,255,0.25)' : cfg.color + '30'}` }}>
        <IconComp sx={{ fontSize: 20, color: isMe ? '#fff' : cfg.color }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: isMe ? '#fff' : '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Typography>
        <Chip label={cfg.label} size="small" sx={{ fontSize: 9, height: 15, mt: 0.3, bgcolor: isMe ? 'rgba(255,255,255,0.2)' : cfg.color + '18', color: isMe ? '#fff' : cfg.color, fontWeight: 700, '& .MuiChip-label': { px: 0.6 } }} />
      </Box>
      <ShareMenu url={url} name={name} onSnack={onSnack} />
    </Box>
  );
}

// ─── File Preview Card ────────────────────────────────────────────────────────

function FilePreviewCard({ url, name, size, isMe, onSnack, compact = false }) {
  const category = getFileCategory(name);
  const cfg = FILE_CONFIG[category] || FILE_CONFIG.file;
  const IconComp = cfg.icon;

  if (category === 'image') {
    return (
      <Box>
        <a href={url} target="_blank" rel="noopener noreferrer">
          <Box component="img" src={url} alt={name || 'image'}
            sx={{ maxWidth: 220, maxHeight: 180, borderRadius: 2, display: 'block', objectFit: 'cover', cursor: 'pointer', border: '1px solid rgba(0,0,0,0.06)' }}
            onError={e => { e.target.style.display = 'none'; }} />
        </a>
        {name && <Typography sx={{ fontSize: 11, color: isMe ? 'rgba(255,255,255,0.6)' : '#aaa', mt: 0.5 }}>{name}</Typography>}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.3 }}><ShareMenu url={url} name={name} onSnack={onSnack} /></Box>
      </Box>
    );
  }

  if (category === 'video') {
    return (
      <Box>
        <Box component="video" controls sx={{ maxWidth: 240, maxHeight: 160, borderRadius: 2, display: 'block' }}>
          <source src={url} /><a href={url} target="_blank" rel="noopener noreferrer">{name}</a>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}><ShareMenu url={url} name={name} onSnack={onSnack} /></Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: compact ? '8px 10px' : '10px 14px', borderRadius: 2, minWidth: 180, maxWidth: 260, bgcolor: isMe ? 'rgba(255,255,255,0.12)' : cfg.bg, border: isMe ? '1px solid rgba(255,255,255,0.18)' : `1px solid ${cfg.color}22` }}>
      <Box sx={{ width: 40, height: 40, borderRadius: 1.5, flexShrink: 0, bgcolor: isMe ? 'rgba(255,255,255,0.18)' : cfg.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${isMe ? 'rgba(255,255,255,0.25)' : cfg.color + '30'}` }}>
        <IconComp sx={{ fontSize: 22, color: isMe ? '#fff' : cfg.color }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: isMe ? '#fff' : '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>{name || 'File'}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mt: 0.35 }}>
          <Chip label={cfg.label} size="small" sx={{ fontSize: 9, height: 16, px: 0.5, bgcolor: isMe ? 'rgba(255,255,255,0.2)' : cfg.color + '18', color: isMe ? '#fff' : cfg.color, fontWeight: 700, letterSpacing: 0.3, '& .MuiChip-label': { px: 0.6 } }} />
          {size && <Typography sx={{ fontSize: 11, color: isMe ? 'rgba(255,255,255,0.55)' : '#aaa' }}>{formatFileSize(size)}</Typography>}
        </Box>
      </Box>
      <ShareMenu url={url} name={name} onSnack={onSnack} />
    </Box>
  );
}

// ─── Message Bubble ───────────────────────────────────────────────────────────

function MessageBubble({ msg, isMe, showSender, onSnack }) {
  const color = getAvatarColor(msg.sender?.name || '');

  const renderContent = () => {
    if (msg.message_type === 'text') {
      const links = parseTextForLinks(msg.content || '');
      const fileLinkCards = links.map(l => ({ ...l, category: getCategoryFromUrl(l.url) })).filter(l => l.category !== null);
      return (
        <Box>
          <Typography sx={{ fontSize: 14, lineHeight: 1.55, color: isMe ? '#fff' : '#1a1a2e', wordBreak: 'break-word' }}>{msg.content}</Typography>
          {fileLinkCards.map((l, i) => <LinkFileCard key={i} url={l.url} isMe={isMe} onSnack={onSnack} />)}
        </Box>
      );
    }
    return <FilePreviewCard url={msg.file_url} name={msg.file_name} size={msg.file_size} isMe={isMe} onSnack={onSnack} />;
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', mb: 0.5, alignItems: 'flex-end', gap: 1 }}>
      {!isMe && (
        <Avatar sx={{ width: 26, height: 26, flexShrink: 0, bgcolor: color + '22', color, border: `1px solid ${color}30`, fontSize: 10, fontWeight: 700 }}>
          {getInitials(msg.sender?.name || '?')}
        </Avatar>
      )}
      <Box sx={{ maxWidth: '72%', p: msg.message_type === 'text' ? '9px 13px' : '8px', borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px', bgcolor: isMe ? '#7c3aed' : '#ffffff', border: isMe ? 'none' : '1px solid #e8e8ec', boxShadow: isMe ? '0 2px 8px rgba(124,58,237,0.22)' : '0 1px 3px rgba(0,0,0,0.05)' }}>
        {showSender && !isMe && <Typography sx={{ fontSize: 11, color, fontWeight: 700, mb: 0.4 }}>{msg.sender?.name}</Typography>}
        {renderContent()}
        <Typography sx={{ fontSize: 10, mt: 0.5, textAlign: 'right', color: isMe ? 'rgba(255,255,255,0.55)' : '#ccc' }}>{formatTime(msg.created)}</Typography>
      </Box>
    </Box>
  );
}

// ─── Modals ───────────────────────────────────────────────────────────────────

function CreateGroupModal({ users, onClose, onCreate, onSnack }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const filtered = users.filter(u =>
    (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );
  const toggle = uid => setSelected(s => s.includes(uid) ? s.filter(x => x !== uid) : [...s, uid]);

  async function submit() {
    if (!name.trim()) return;
    setLoading(true);
    try { await onCreate({ name: name.trim(), description, member_ids: selected }); onSnack('Group created!', 'success'); onClose(); }
    catch (err) { onSnack(err?.response?.data?.error || 'Failed to create group', 'error'); }
    finally { setLoading(false); }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Create New Group</DialogTitle>
      <DialogContent>
        <TextField margin="dense" label="Group Name *" fullWidth value={name} onChange={e => setName(e.target.value)} />
        <TextField margin="dense" label="Description" fullWidth value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" />
        <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 600, color: '#666' }}>Add Members ({selected.length} selected)</Typography>
        <TextField fullWidth size="small" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} sx={{ mb: 1 }} />
        <Paper variant="outlined" sx={{ maxHeight: 200, overflowY: 'auto', borderRadius: 2 }}>
          {filtered.length === 0
            ? <Typography sx={{ p: 2, textAlign: 'center', color: '#bbb', fontSize: 13 }}>No users found</Typography>
            : filtered.map(u => (
              <ListItemButton key={u.id} onClick={() => toggle(u.id)} sx={{ py: 1, gap: 1.5 }}>
                <UserAvatar name={u.name} size={32} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{u.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#bbb' }}>{u.designation || u.email}</Typography>
                </Box>
                {selected.includes(u.id) && <CheckCircle sx={{ color: '#7c3aed', fontSize: 20 }} />}
              </ListItemButton>
            ))}
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={loading || !name.trim()}>
          {loading ? <CircularProgress size={18} /> : 'Create Group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddMembersModal({ users, existingMemberIds, onClose, onAdd, onSnack }) {
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const available = users.filter(u =>
    !existingMemberIds.includes(String(u.id)) &&
    ((u.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase()))
  );
  const toggle = uid => setSelected(s => s.includes(uid) ? s.filter(x => x !== uid) : [...s, uid]);

  async function submit() {
    if (!selected.length) return;
    setLoading(true);
    try { await onAdd(selected); onSnack('Members added!', 'success'); onClose(); }
    catch (err) { onSnack(err?.response?.data?.error || 'Failed to add members', 'error'); }
    finally { setLoading(false); }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Add Members</DialogTitle>
      <DialogContent>
        <TextField fullWidth size="small" placeholder="Search people…" value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ mb: 1, mt: 1 }} />
        <Paper variant="outlined" sx={{ maxHeight: 220, overflowY: 'auto', borderRadius: 2 }}>
          {available.length === 0
            ? <Typography sx={{ p: 2, textAlign: 'center', color: '#bbb', fontSize: 13 }}>{search ? 'No results' : 'Everyone is already a member'}</Typography>
            : available.map(u => (
              <ListItemButton key={u.id} onClick={() => toggle(u.id)} sx={{ py: 1, gap: 1.5 }}>
                <UserAvatar name={u.name} size={32} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{u.name}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#bbb' }}>{u.designation || u.email}</Typography>
                </Box>
                {selected.includes(u.id) && <CheckCircle sx={{ color: '#7c3aed', fontSize: 20 }} />}
              </ListItemButton>
            ))}
        </Paper>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={loading || !selected.length}>
          {loading ? <CircularProgress size={18} /> : `Add${selected.length ? ` (${selected.length})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeleteGroupModal({ groupName, onClose, onConfirm, onSnack }) {
  const [loading, setLoading] = useState(false);
  async function submit() {
    setLoading(true);
    try { await onConfirm(); onSnack('Group deleted.', 'success'); onClose(); }
    catch (err) { onSnack(err?.response?.data?.error || 'Failed to delete group', 'error'); }
    finally { setLoading(false); }
  }
  return (
    <Dialog open onClose={onClose}>
      <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>Delete Group</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: '#555', lineHeight: 1.6 }}>
          Are you sure you want to delete <strong>"{groupName}"</strong>?
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#bbb', mt: 1 }}>This cannot be undone.</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={submit} variant="contained" color="error" disabled={loading}>
          {loading ? <CircularProgress size={18} /> : 'Delete Group'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Members Panel ────────────────────────────────────────────────────────────

function MembersPanel({ group, currentUser, isAdmin, onRemoveMember, onAddMembers, onSnack, onClose, isMobile }) {
  const [removing, setRemoving] = useState(null);

  async function handleRemove(member) {
    if (!window.confirm(`Remove ${member.user?.name} from the group?`)) return;
    setRemoving(member.user?.id);
    try { await onRemoveMember(member.user?.id); onSnack(`${member.user?.name} removed.`, 'success'); }
    catch (err) { onSnack(err?.response?.data?.error || 'Failed to remove member', 'error'); }
    finally { setRemoving(null); }
  }

  const content = (
    <Box sx={{ width: isMobile ? '100%' : 255, height: '100%', flexShrink: 0, borderLeft: isMobile ? 'none' : '1px solid #e4e4e8', bgcolor: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ px: 1.5, py: 1.5, borderBottom: '1px solid #e4e4e8', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 1 }}>
          Members · {group.members?.length || 0}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isAdmin && (
            <Tooltip title="Add members">
              <IconButton size="small" onClick={onAddMembers} sx={{ bgcolor: '#f0eeff', border: '1px solid #c4b5fd', color: '#7c3aed', '&:hover': { bgcolor: '#ede9fe' } }}>
                <PersonAdd fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {isMobile && <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>}
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
        {(group.members || []).map(m => {
          const isSelf = String(m.user?.id) === String(currentUser?.id);
          const isAdminRole = m.role === 'admin';
          return (
            <Box key={m.id || m.user?.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.2, p: '9px 10px', borderRadius: 2, mb: 0.2 }}>
              <UserAvatar name={m.user?.name || ''} size={33} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{m.user?.name}{isSelf ? ' (You)' : ''}</Typography>
                <Typography sx={{ fontSize: 11, color: '#bbb', mt: 0.3 }}>{m.user?.designation || m.user?.email}</Typography>
              </Box>
              {isAdminRole && <Chip label="admin" size="small" sx={{ fontSize: 10, bgcolor: '#f0eeff', color: '#7c3aed', fontWeight: 700 }} />}
              {isAdmin && !isSelf && !isAdminRole && (
                <Button size="small" color="error" variant="outlined"
                  sx={{ fontSize: 11, py: 0.2, px: 1, minWidth: 'unset' }}
                  onClick={() => handleRemove(m)} disabled={removing === m.user?.id}>
                  {removing === m.user?.id ? <CircularProgress size={10} /> : 'Remove'}
                </Button>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  if (isMobile) return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: '85vw', maxWidth: 320 } }}>
      {content}
    </Drawer>
  );
  return content;
}

// ─── Sidebar Content ──────────────────────────────────────────────────────────

function SidebarContent({ tab, setTab, search, setSearch, sortedDirectConvos, sortedGroups, filteredUsers, activeChat, unreadCounts, chatKey, openDirectConvo, openGroup, startDirectChat, setModal, isMobile, onClose, onRefresh, refreshing }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
      <Box sx={{ px: 2, pt: 2.5, pb: 1.5, borderBottom: '1px solid #e4e4e8', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', letterSpacing: '-0.4px', fontSize: { xs: 16, sm: 18 } }}>
            💬 Messages
          </Typography>
        </Box>
        <Tooltip title="Refresh conversations">
          <span>
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={refreshing}
              sx={{
                color: '#7c3aed',
                bgcolor: '#f0eeff',
                border: '1px solid #c4b5fd',
                '&:hover': { bgcolor: '#ede9fe', borderColor: '#a78bfa' },
                '&.Mui-disabled': { bgcolor: '#f5f5f7', color: '#ccc', border: '1px solid #e4e4e8' },
                '& svg': {
                  transition: 'transform 0.3s ease',
                  animation: refreshing ? 'spin 0.7s linear infinite' : 'none',
                  '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                },
              }}>
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {isMobile && <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>}
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => { setTab(v); setSearch(''); }}
        variant="fullWidth"
        sx={{
          borderBottom: '1px solid #e4e4e8',
          '& .MuiTab-root': { fontSize: 11, minHeight: 42, textTransform: 'none', fontWeight: 500, px: 0.5 },
          '& .Mui-selected': { color: '#7c3aed !important', fontWeight: 700 },
          '& .MuiTabs-indicator': { backgroundColor: '#7c3aed' },
        }}>
        <Tab label="Direct" icon={<ChatIcon sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ minHeight: 42 }} />
        <Tab label="Groups" icon={<Group sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ minHeight: 42 }} />
        <Tab label="People" icon={<People sx={{ fontSize: 16 }} />} iconPosition="start" sx={{ minHeight: 42 }} />
      </Tabs>

      <Box sx={{ px: 1.5, pt: 1.2, pb: 0.5 }}>
        <TextField
          fullWidth size="small" value={search} onChange={e => setSearch(e.target.value)}
          placeholder={tab === 0 ? 'Search…' : tab === 1 ? 'Search groups…' : 'Search people…'}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: '#bbb' }} /></InputAdornment>,
            sx: { borderRadius: 2, bgcolor: '#f9f9fb', fontSize: 13 },
          }} />
      </Box>

      {tab === 1 && (
        <Box sx={{ px: 1.5, pb: 0.5 }}>
          <Button fullWidth variant="outlined" startIcon={<Group />} onClick={() => setModal('createGroup')}
            sx={{ borderRadius: 2, borderColor: '#c4b5fd', color: '#7c3aed', bgcolor: '#f0eeff', fontWeight: 600, fontSize: 13, '&:hover': { bgcolor: '#ede9fe', borderColor: '#a78bfa' } }}>
            New Group
          </Button>
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto' }}>

        {/* ── Direct ── */}
        {tab === 0 && (
          sortedDirectConvos.length === 0
            ? <Typography sx={{ p: 3, textAlign: 'center', fontSize: 13, color: '#ccc' }}>{search ? 'No results' : 'No conversations yet.'}</Typography>
            : sortedDirectConvos.map(c => {
              const other = c.other_user;
              const isActive = activeChat?.id === c.id && activeChat?.type === 'direct';
              const unread = unreadCounts[chatKey('direct', c.id)] || 0;
              return (
                <ListItemButton
                  key={c.id}
                  selected={isActive}
                  onClick={() => { openDirectConvo(c); if (isMobile) onClose?.(); }}
                  sx={{
                    py: 1.3, px: 2, gap: 1.5, alignItems: 'center',
                    borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent',
                    '&.Mui-selected': { bgcolor: '#f0eeff' },
                    '&.Mui-selected:hover': { bgcolor: '#ede9fe' },
                  }}>
                  <Badge badgeContent={unread} color="primary" max={99} sx={{ '& .MuiBadge-badge': { bgcolor: '#7c3aed', fontSize: 10 } }}>
                    <UserAvatar name={other?.name || ''} size={42} />
                  </Badge>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: unread > 0 ? 700 : 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {other?.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#aaa', mt: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {other?.designation || other?.email || ''}
                    </Typography>
                  </Box>
                  {unread > 0 && (
                    <Box sx={{ minWidth: 20, height: 20, borderRadius: 10, bgcolor: '#7c3aed', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5, flexShrink: 0 }}>
                      {unread > 99 ? '99+' : unread}
                    </Box>
                  )}
                </ListItemButton>
              );
            })
        )}

        {/* ── Groups ── */}
        {tab === 1 && (
          sortedGroups.length === 0
            ? <Typography sx={{ p: 3, textAlign: 'center', fontSize: 13, color: '#ccc' }}>{search ? 'No results' : 'No groups yet.'}</Typography>
            : sortedGroups.map(g => {
              const isActive = activeChat?.id === g.id && activeChat?.type === 'group';
              const unread = unreadCounts[chatKey('group', g.id)] || 0;
              return (
                <ListItemButton
                  key={g.id}
                  selected={isActive}
                  onClick={() => { openGroup(g); if (isMobile) onClose?.(); }}
                  sx={{
                    py: 1.3, px: 2, gap: 1.5, alignItems: 'center',
                    borderLeft: isActive ? '3px solid #7c3aed' : '3px solid transparent',
                    '&.Mui-selected': { bgcolor: '#f0eeff' },
                    '&.Mui-selected:hover': { bgcolor: '#ede9fe' },
                  }}>
                  <Badge badgeContent={unread} color="primary" max={99} sx={{ '& .MuiBadge-badge': { bgcolor: '#7c3aed', fontSize: 10 } }}>
                    <Avatar sx={{ width: 42, height: 42, bgcolor: '#2563eb22', color: '#2563eb', border: '1.5px solid #2563eb44', fontSize: 20 }}>👥</Avatar>
                  </Badge>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: unread > 0 ? 700 : 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {g.name}
                      </Typography>
                      {g.my_role === 'admin' && (
                        <Chip label="admin" size="small" sx={{ fontSize: 10, bgcolor: '#f0eeff', color: '#7c3aed', fontWeight: 700 }} />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: 12, color: '#aaa', mt: 0.2 }}>
                      {g.member_count || 0} member{g.member_count !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                  {unread > 0 && (
                    <Box sx={{ minWidth: 20, height: 20, borderRadius: 10, bgcolor: '#7c3aed', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 0.5, flexShrink: 0 }}>
                      {unread > 99 ? '99+' : unread}
                    </Box>
                  )}
                </ListItemButton>
              );
            })
        )}

        {/* ── People ── */}
        {tab === 2 && (
          filteredUsers.length === 0
            ? <Typography sx={{ p: 3, textAlign: 'center', fontSize: 13, color: '#ccc' }}>No users found</Typography>
            : filteredUsers.map(u => (
              <ListItemButton
                key={u.id}
                onClick={() => { startDirectChat(u); if (isMobile) onClose?.(); }}
                sx={{ py: 1.2, px: 2, gap: 1.5, alignItems: 'center' }}>
                <UserAvatar name={u.name} size={42} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#aaa', mt: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.designation || u.email}</Typography>
                </Box>
              </ListItemButton>
            ))
        )}
      </Box>
    </Box>
  );
}

// ─── Notification WS hook ─────────────────────────────────────────────────────

function useNotificationSocket({ currentUser, activeChatRef, setUnreadCounts, setDirectConvos, setGroups, chatKey }) {
  const wsRef = useRef(null);

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    let retryTimer = null;
    let retryDelay = 2000;

    async function connect() {
      if (cancelled) return;
      try {
        // const token = await getToken();
        const token = getToken();
        const wsBase = getWsBase();
        const url = `${wsBase}/ws/chat/notifications/?token=${token}`;
        console.log('[NotifWS] connecting to:', url);
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) { ws.close(); return; }
          console.log('[NotifWS] connected');
          retryDelay = 2000;
        };

        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.type === 'unread_update') {
              const { chat_type, chat_id, unread_count, last_message } = data;
              const active = activeChatRef.current;
              const isOpen = active && active.type === chat_type && String(active.id) === String(chat_id);

              if (chat_type === 'direct') {
                setDirectConvos(prev => prev.map(c => String(c.id) === String(chat_id) ? { ...c, last_message } : c));
              } else {
                setGroups(prev => prev.map(g => String(g.id) === String(chat_id) ? { ...g, last_message } : g));
              }

              if (!isOpen) {
                setUnreadCounts(prev => ({ ...prev, [chatKey(chat_type, chat_id)]: unread_count }));
              }
            }
          } catch (err) {
            console.error('[NotifWS] parse error:', err);
          }
        };

        ws.onerror = (err) => { console.error('[NotifWS] error:', err); };

        ws.onclose = (ev) => {
          console.warn('[NotifWS] closed, code:', ev.code);
          if (!cancelled) {
            retryTimer = setTimeout(() => {
              retryDelay = Math.min(retryDelay * 1.5, 15000);
              connect();
            }, retryDelay);
          }
        };
      } catch (err) {
        console.error('[NotifWS] connect error:', err);
        if (!cancelled) {
          retryTimer = setTimeout(() => {
            retryDelay = Math.min(retryDelay * 1.5, 15000);
            connect();
          }, retryDelay);
        }
      }
    }

    connect();

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, [currentUser?.id]);
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ChatApplication({ currentUser }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [directConvos, setDirectConvos] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [wsReady, setWsReady] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [modal, setModal] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'info' });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // ── Message limit: default 20, options: 20 | 50 | 100 | 'all' | custom int
  const [msgLimit, setMsgLimit] = useState(20);
  const [historyLoading, setHistoryLoading] = useState(false);

  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const activeChatRef = useRef(null);
  activeChatRef.current = activeChat;

  const showSnack = useCallback((msg, severity = 'info') => setSnack({ open: true, msg, severity }), []);
  const chatKey = useCallback((type, id) => `${type}:${id}`, []);

  useNotificationSocket({ currentUser, activeChatRef, setUnreadCounts, setDirectConvos, setGroups, chatKey });

  // Initial data load
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [usersRes, groupsRes, directRes, unreadRes] = await Promise.all([
          api.get('/chat/users/'),
          api.get('/chat/groups/'),
          api.get('/chat/direct/'),
          api.get('/chat/unread/'),
        ]);
        setUsers(usersRes.data);
        setGroups(groupsRes.data);
        setDirectConvos(directRes.data);
        const counts = {};
        (unreadRes.data || []).forEach(item => {
          counts[chatKey(item.type, item.id)] = item.unread_count;
        });
        setUnreadCounts(counts);
      } catch (err) {
        console.error('Failed to load chat data:', err);
      }
    };
    fetchAll();
  }, []);

  // ── Fetch message history via REST with chosen limit ────────────────────────
  // limit param: integer (e.g. 20) → ?limit=20
  //              'all'             → ?limit=all  (backend returns everything)
  const fetchHistory = useCallback(async (chat, limit) => {
    if (!chat) return;
    setHistoryLoading(true);
    try {
      const limitParam = limit === 'all' ? 'all' : limit;
      const res = await api.get(`/chat/messages/${chat.type}/${chat.id}/?limit=${limitParam}`);
      setMessages(res.data || []);
    } catch (err) {
      console.error('Failed to fetch message history:', err);
      showSnack('Failed to load messages', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [showSnack]);

  // Chat WebSocket — reconnects when activeChat changes
  useEffect(() => {
    if (!activeChat) return;
    setMessages([]); setWsReady(false);
    if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }

    let retryCount = 0;
    const MAX_RETRIES = 1;
    let cancelled = false;

    async function connect() {
      if (cancelled) return;
      try {
        // const token = await getToken();
        const token = getToken();
        const wsBase = getWsBase();
        const url = `${wsBase}/ws/chat/${activeChat.type}/${activeChat.id}/?token=${token}`;
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) { ws.close(); return; }
          setWsReady(true);
          retryCount = 0;
          setUnreadCounts(prev => {
            const next = { ...prev };
            delete next[chatKey(activeChat.type, activeChat.id)];
            return next;
          });
          api.post(`/chat/new-read/${activeChat.type}/${activeChat.id}/read/`).catch(() => {});
        };

        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            if (data.type === 'history') {
              // Ignore WS history — we use REST fetch with our chosen limit
            } else if (data.type === 'message') {
              const msg = data.message;
              setMessages(prev => [...prev, msg]);
              if (activeChat.type === 'direct') {
                setDirectConvos(prev => prev.map(c => String(c.id) === String(activeChat.id) ? { ...c, last_message: msg } : c));
              } else {
                setGroups(prev => prev.map(g => String(g.id) === String(activeChat.id) ? { ...g, last_message: msg } : g));
              }
            }
          } catch {}
        };

        ws.onerror = () => { setWsReady(false); };

        // ws.onclose = async (ev) => {
        //   if (cancelled) return;
        //   setWsReady(false);
        //   if ((ev.code === 1006 || ev.code === 4001) && retryCount < MAX_RETRIES) {
        //     retryCount++;
        //     try { await refreshAccessToken(); connect(); }
        //     catch { showSnack('Session expired. Please log in again.', 'error'); }
        //   }

        ws.onclose = async (ev) => {
  if (cancelled) return;
  setWsReady(false);
  if ((ev.code === 1006 || ev.code === 4001) && retryCount < MAX_RETRIES) {
    retryCount++;
    setTimeout(() => connect(), 2000);
  }
        };
      } catch {
        showSnack('Failed to connect to chat.', 'error');
      }
    }

    connect();
    // Fetch history via REST with current limit
    fetchHistory(activeChat, msgLimit);

    return () => {
      cancelled = true;
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, [activeChat?.id]);

  // Re-fetch history when msgLimit changes (only if a chat is open)
  useEffect(() => {
    if (activeChat) {
      fetchHistory(activeChat, msgLimit);
    }
  }, [msgLimit]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Derived lists ──────────────────────────────────────────────────────────
  const q = search.toLowerCase();
  const sortedDirectConvos = [...directConvos]
    .sort((a, b) => getLastMessageTime(b) - getLastMessageTime(a))
    .filter(c => (c.other_user?.name || '').toLowerCase().includes(q));
  const sortedGroups = [...groups]
    .sort((a, b) => getLastMessageTime(b) - getLastMessageTime(a))
    .filter(g => (g.name || '').toLowerCase().includes(q));
  const filteredUsers = users.filter(u =>
    (u.name || '').toLowerCase().includes(q) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  // ── Chat actions ────────────────────────────────────────────────────────────

  async function startDirectChat(user) {
    try {
      const r = await api.post('/chat/direct/', { user_id: user.id });
      const convo = r.data;
      setDirectConvos(prev => prev.find(c => c.id === convo.id) ? prev : [convo, ...prev]);
      setActiveChat({ type: 'direct', id: convo.id, name: user.name, data: convo });
      setTab(0); setSearch('');
    } catch (err) {
      showSnack(err?.response?.data?.error || 'Failed to start chat', 'error');
    }
  }

  function openDirectConvo(convo) {
    setActiveChat({ type: 'direct', id: convo.id, name: convo.other_user?.name || 'Direct', data: convo });
    setShowMembers(false);
    setPendingFile(null);
  }

  function openGroup(group) {
    setActiveChat({ type: 'group', id: group.id, name: group.name, data: group });
    setShowMembers(false);
    setPendingFile(null);
  }

  function closeChat() {
    if (wsRef.current) wsRef.current.close();
    setActiveChat(null);
    setMessages([]);
    setWsReady(false);
    setShowMembers(false);
    setPendingFile(null);
    if (isMobile) setSidebarOpen(true);
  }

  async function handleCreateGroup(data) {
    const r = await api.post('/chat/groups/', data);
    const newGroup = r.data;
    setGroups(prev => [newGroup, ...prev]);
    openGroup(newGroup);
    setTab(1);
  }

  async function handleAddMembers(userIds) {
    if (!activeChat) return;
    await api.post(`/chat/groups/${activeChat.id}/members/`, { user_ids: userIds });
    const r = await api.get(`/chat/groups/${activeChat.id}/`);
    const updated = r.data;
    setGroups(prev => prev.map(g => g.id === updated.id ? updated : g));
    setActiveChat(prev => ({ ...prev, data: updated }));
  }

  async function handleRemoveMember(userId) {
    if (!activeChat) return;
    await api.delete(`/chat/groups/${activeChat.id}/members/`, { data: { user_id: userId } });
    const r = await api.get(`/chat/groups/${activeChat.id}/`);
    const updated = r.data;
    setGroups(prev => prev.map(g => g.id === updated.id ? updated : g));
    setActiveChat(prev => ({ ...prev, data: updated }));
  }

  async function handleDeleteGroup() {
    if (!activeChat) return;
    await api.delete(`/chat/groups/${activeChat.id}/`);
    setGroups(prev => prev.filter(g => g.id !== activeChat.id));
    closeChat();
  }

  function wsSend(payload) {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }

  function sendText() {
    const content = text.trim();
    if (!content) return;
    if (wsSend({ type: 'text', content })) setText('');
  }

  async function sendFile() {
    if (!pendingFile || !activeChat) return;
    if (wsRef.current?.readyState !== WebSocket.OPEN) { showSnack('Connection not ready.', 'warning'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', pendingFile.file);
      form.append('file_type', pendingFile.type);
      const r = await api.post('/chat/upload/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { file_url, file_name, file_size, file_type } = r.data;
      wsSend({ type: file_type, file_url, file_name, file_size });
      setPendingFile(null); setText('');
      showSnack('File sent!', 'success');
    } catch (err) {
      showSnack(err?.response?.data?.error || 'Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (pendingFile) sendFile(); else sendText();
    }
  }

  const handleRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const [usersRes, groupsRes, directRes, unreadRes] = await Promise.all([
        api.get('/chat/users/'),
        api.get('/chat/groups/'),
        api.get('/chat/direct/'),
        api.get('/chat/unread/'),
      ]);
      setUsers(usersRes.data);
      setGroups(groupsRes.data);
      setDirectConvos(directRes.data);
      const counts = {};
      (unreadRes.data || []).forEach(item => { counts[chatKey(item.type, item.id)] = item.unread_count; });
      setUnreadCounts(counts);
      showSnack('Refreshed successfully', 'success');
    } catch (err) {
      showSnack('Failed to refresh data', 'error');
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, chatKey, showSnack]);

  function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    let type = 'document';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) type = 'image';
    else if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) type = 'video';
    const maxSize = type === 'image' ? 2 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) { showSnack(type === 'image' ? 'Images must be under 2 MB' : 'Files must be under 10 MB', 'error'); return; }
    setPendingFile({ file, type, preview: type === 'image' ? URL.createObjectURL(file) : null });
  }

  const isGroupAdmin = activeChat?.type === 'group' && activeChat?.data?.my_role === 'admin';
  const activeGroup = activeChat?.type === 'group' ? activeChat.data : null;
  const existingMemberIds = (activeGroup?.members || []).map(m => String(m.user?.id));
  const canSend = (!!text.trim() || !!pendingFile) && (wsReady || !!pendingFile);

  function renderPendingFilePreview() {
    if (!pendingFile) return null;
    const category = getFileCategory(pendingFile.file.name);
    const cfg = FILE_CONFIG[category] || FILE_CONFIG.file;
    const IconComp = cfg.icon;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '8px 12px', bgcolor: '#f5f5f7', borderRadius: 2, border: `1px solid ${cfg.color}22` }}>
        {pendingFile.preview
          ? <Box component="img" src={pendingFile.preview} sx={{ width: 44, height: 44, borderRadius: 1.5, objectFit: 'cover' }} />
          : <Box sx={{ width: 44, height: 44, borderRadius: 1.5, flexShrink: 0, bgcolor: cfg.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${cfg.color}25` }}>
            <IconComp sx={{ fontSize: 22, color: cfg.color }} />
          </Box>
        }
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pendingFile.file.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.8, alignItems: 'center', mt: 0.3 }}>
            <Chip label={cfg.label} size="small" sx={{ fontSize: 9, height: 16, bgcolor: cfg.color + '15', color: cfg.color, fontWeight: 700, '& .MuiChip-label': { px: 0.6 } }} />
            <Typography sx={{ fontSize: 11, color: '#aaa' }}>{formatFileSize(pendingFile.file.size)}</Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={() => setPendingFile(null)} sx={{ color: '#bbb' }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  const sidebarProps = {
    tab, setTab, search, setSearch,
    sortedDirectConvos, sortedGroups, filteredUsers,
    activeChat, unreadCounts, chatKey,
    openDirectConvo, openGroup, startDirectChat, setModal,
    onRefresh: handleRefresh,
    refreshing,
  };

  return (
    <Box sx={{ display: 'flex', height: '100dvh', bgcolor: '#f5f5f7', overflow: 'hidden' }}>

      {/* ── Desktop sidebar ── */}
      {!isMobile && (
        <Box sx={{ width: 300, minWidth: 260, display: 'flex', flexDirection: 'column', bgcolor: '#fff', borderRight: '1px solid #e4e4e8', flexShrink: 0 }}>
          <SidebarContent {...sidebarProps} isMobile={false} />
        </Box>
      )}

      {/* ── Mobile sidebar drawer ── */}
      {isMobile && (
        <SwipeableDrawer
          anchor="left"
          open={sidebarOpen}
          onOpen={() => setSidebarOpen(true)}
          onClose={() => setSidebarOpen(false)}
          PaperProps={{ sx: { width: '85vw', maxWidth: 320 } }}
          disableSwipeToOpen={!!activeChat}>
          <SidebarContent {...sidebarProps} isMobile onClose={() => setSidebarOpen(false)} />
        </SwipeableDrawer>
      )}

      {/* ── Main chat area ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#f9f9fb', minWidth: 0 }}>

        {!activeChat ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, px: 2 }}>
            <Typography sx={{ fontSize: 48, opacity: 0.2 }}>💬</Typography>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: '#bbb' }}>Select a conversation</Typography>
            <Typography sx={{ fontSize: 13, color: '#d0d0d8', textAlign: 'center' }}>
              Pick a direct chat or group, or find someone in People
            </Typography>
            {isMobile && (
              <Button variant="outlined" onClick={() => setSidebarOpen(true)}
                sx={{ mt: 1, borderColor: '#c4b5fd', color: '#7c3aed', borderRadius: 2 }}>
                Open conversations
              </Button>
            )}
          </Box>
        ) : (
          <>
            {/* ── Chat header ── */}
            <Box sx={{
              px: { xs: 1.5, sm: 2.5 }, py: 1.5,
              borderBottom: '1px solid #e4e4e8', bgcolor: '#fff',
              display: 'flex', alignItems: 'center', gap: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              flexWrap: 'nowrap',
            }}>
              {isMobile && (
                <Tooltip title="Back">
                  <IconButton size="small" onClick={closeChat} sx={{ mr: 0.5 }}>
                    <ArrowBack fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {activeChat.type === 'group'
                ? <Avatar sx={{ width: 36, height: 36, bgcolor: '#2563eb22', color: '#2563eb', border: '1.5px solid #2563eb44', fontSize: 18, flexShrink: 0 }}>👥</Avatar>
                : <UserAvatar name={activeChat.name} size={36} />
              }
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 700, color: '#1a1a2e', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {activeChat.name}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#999', mt: 0.1 }}>
                  {activeChat.type === 'group'
                    ? `${activeChat.data?.member_count || 0} members${isGroupAdmin ? ' · Admin' : ''}`
                    : activeChat.data?.other_user?.designation || activeChat.data?.other_user?.email || ''
                  }
                </Typography>
              </Box>

              {/* ── Message Limit Dropdown ── */}
              <MessageLimitSelector value={msgLimit} onChange={setMsgLimit} />

              {activeChat.type === 'group' && isGroupAdmin && (
                <Tooltip title="Delete group">
                  <IconButton size="small" onClick={() => setModal('deleteGroup')}
                    sx={{ bgcolor: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', '&:hover': { bgcolor: '#fee2e2' } }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {activeChat.type === 'group' && (
                <Tooltip title="Members">
                  <IconButton size="small" onClick={() => setShowMembers(s => !s)}
                    sx={{ bgcolor: showMembers ? '#f0eeff' : '#f5f5f7', border: showMembers ? '1px solid #c4b5fd' : '1px solid #e4e4e8', color: showMembers ? '#7c3aed' : '#888' }}>
                    <Group fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!isMobile && (
                <Tooltip title="Close">
                  <IconButton size="small" onClick={closeChat} sx={{ bgcolor: '#f5f5f7', border: '1px solid #e4e4e8', color: '#888' }}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* ── Messages + Members panel ── */}
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: '16px 12px 10px', sm: '20px 20px 12px' }, display: 'flex', flexDirection: 'column', gap: 0.3 }}>

                {historyLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                    <CircularProgress size={22} sx={{ color: '#7c3aed' }} />
                    <Typography sx={{ ml: 1.5, fontSize: 13, color: '#aaa' }}>Loading messages…</Typography>
                  </Box>
                )}

                {!historyLoading && messages.length === 0 && (
                  <Typography sx={{ textAlign: 'center', color: '#ccc', fontSize: 13, mt: 3 }}>
                    No messages yet. Say hello 👋
                  </Typography>
                )}

                {!historyLoading && Object.entries(groupMessagesByDate(messages)).map(([date, msgs]) => (
                  <React.Fragment key={date}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1.5 }}>
                      <Divider sx={{ flex: 1 }} />
                      <Chip label={date} size="small" sx={{ fontSize: 11, bgcolor: '#fff', border: '1px solid #e8e8ec', color: '#bbb' }} />
                      <Divider sx={{ flex: 1 }} />
                    </Box>
                    {msgs.map((msg, i) => {
                      const isMe = String(msg.sender?.id) === String(currentUser?.id);
                      const prev = msgs[i - 1];
                      const showSender = activeChat.type === 'group' && (!prev || prev.sender?.id !== msg.sender?.id);
                      return <MessageBubble key={msg.id} msg={msg} isMe={isMe} showSender={showSender} onSnack={showSnack} />;
                    })}
                  </React.Fragment>
                ))}
                <div ref={messagesEndRef} />
              </Box>

              {showMembers && activeGroup && (
                <MembersPanel
                  group={activeGroup}
                  currentUser={currentUser}
                  isAdmin={isGroupAdmin}
                  onRemoveMember={handleRemoveMember}
                  onAddMembers={() => setModal('addMembers')}
                  onSnack={showSnack}
                  isMobile={isMobile}
                  onClose={() => setShowMembers(false)}
                />
              )}
            </Box>

            {/* ── Input area ── */}
            <Box sx={{ px: { xs: 1, sm: 1.8 }, pt: 1.2, pb: { xs: 1.5, sm: 1.5 }, borderTop: '1px solid #e4e4e8', bgcolor: '#fff', display: 'flex', flexDirection: 'column', gap: 1 }}>
              {renderPendingFilePreview()}
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.8 }}>
                <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect} />
                <Tooltip title="Attach file">
                  <span>
                    <IconButton
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      sx={{ bgcolor: '#f5f5f7', border: '1px solid #e4e4e8', color: '#999', borderRadius: 2, width: { xs: 38, sm: 40 }, height: { xs: 38, sm: 40 }, '&:hover': { bgcolor: '#f0eeff', borderColor: '#c4b5fd', color: '#7c3aed' } }}>
                      <AttachFile fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <TextField
                  multiline maxRows={4} fullWidth size="small"
                  placeholder={!wsReady ? 'Connecting…' : pendingFile ? 'Add a caption (optional)…' : 'Type a message…'}
                  value={text}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={!wsReady && !pendingFile}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#f5f5f7', fontSize: { xs: 13, sm: 14 }, '&.Mui-focused fieldset': { borderColor: '#c4b5fd' } } }}
                />
                <Tooltip title="Send">
                  <span>
                    <IconButton
                      onClick={pendingFile ? sendFile : sendText}
                      disabled={uploading || (!text.trim() && !pendingFile)}
                      sx={{ bgcolor: canSend ? '#7c3aed' : '#f5f5f7', border: canSend ? 'none' : '1px solid #e4e4e8', color: canSend ? '#fff' : '#ccc', borderRadius: 2, width: { xs: 38, sm: 40 }, height: { xs: 38, sm: 40 }, boxShadow: canSend ? '0 2px 8px rgba(124,58,237,0.28)' : 'none', '&:hover': { bgcolor: canSend ? '#6d28d9' : undefined }, '&.Mui-disabled': { bgcolor: '#f5f5f7', color: '#ccc' } }}>
                      {uploading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Send fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {/* ── Modals ── */}
      {modal === 'createGroup' && (
        <CreateGroupModal users={users} onClose={() => setModal(null)} onCreate={handleCreateGroup} onSnack={showSnack} />
      )}
      {modal === 'addMembers' && activeGroup && (
        <AddMembersModal users={users} existingMemberIds={existingMemberIds} onClose={() => setModal(null)} onAdd={handleAddMembers} onSnack={showSnack} />
      )}
      {modal === 'deleteGroup' && activeGroup && (
        <DeleteGroupModal groupName={activeGroup.name} onClose={() => setModal(null)} onConfirm={handleDeleteGroup} onSnack={showSnack} />
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ChatApplication;